/*
 * Copyright (c) CIRROM 2026.
 */

import React from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { AlertColor } from "@mui/material/Alert";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Share from "@mui/icons-material/Share";
import { useTheme } from "@mui/material/styles";
import {
  buildShareEmailUrl,
  copyRecordImageToClipboard,
  ShareDetails,
} from "./helpers/shareUtils";

/*
 * Share / copy get a record out of Cirrom; neither writes to a system of
 * record, so neither earns the action colour - neutral chrome on a divider
 * hairline, exactly as in the suggested-changes modal.
 */
const utilityIconSx = {
  color: "text.secondary",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "divider",
  borderRadius: "4px",
  padding: "6px",
  "&:hover": {
    color: "text.primary",
    borderColor: "text.secondary",
    backgroundColor: "action.hover",
  },
};

/*
 * The pair of utility buttons a review card carries in its footer: share the
 * record by email, or copy it to the clipboard as an image. One component so
 * the record and account cards offer the same two affordances with the same
 * icons, the same tooltips and the same outcomes.
 *
 * The card, not the buttons, is what gets rendered to an image, so the host
 * hands over a ref to the element it wants captured. Feedback goes through
 * onNotify because hosts differ in where they show it - the record card has
 * an inline alert, the account card a snackbar.
 */
export const ShareCopyActions = (props: {
  /** The element rendered to PNG - normally the whole card, brand mark included. */
  targetRef: React.RefObject<HTMLElement | null>;
  details: ShareDetails;
  onNotify: (text: string, severity: AlertColor) => void;
  className?: string;
}) => {
  const theme = useTheme();
  const noun = props.details.entity;
  const Noun = noun === "contact" ? "Contact" : "Account";

  const copyImage = async () => {
    if (!props.targetRef.current) throw new Error("Nothing to render");
    await copyRecordImageToClipboard(
      props.targetRef.current,
      theme.palette.background.default
    );
  };

  const handleCopyImage = async () => {
    try {
      await copyImage();
      props.onNotify(`${Noun} record copied to clipboard as image!`, "success");
    } catch (err) {
      console.error("Failed to copy image to clipboard:", err);
      props.onNotify("Failed to copy image to clipboard", "error");
    }
  };

  const handleShare = async () => {
    let imageOnClipboard = true;
    try {
      await copyImage();
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      imageOnClipboard = false;
    }
    window.open(buildShareEmailUrl(props.details, imageOnClipboard), "_blank");
    props.onNotify(
      imageOnClipboard
        ? `${Noun} record copied to clipboard and email opened!`
        : "Email opened, but failed to copy image to clipboard",
      imageOnClipboard ? "success" : "warning"
    );
  };

  return (
    <div className={props.className} style={{ display: "flex", gap: 8 }}>
      <Tooltip title={`Share ${noun} details via email with image export`}>
        <IconButton
          onClick={handleShare}
          aria-label={`Share ${noun} details via email`}
          sx={utilityIconSx}
        >
          <Share fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={`Copy ${noun} record image to clipboard`}>
        <IconButton
          onClick={handleCopyImage}
          aria-label={`Copy ${noun} record image to clipboard`}
          sx={utilityIconSx}
        >
          <ContentCopy fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );
};
